import { z } from "zod";
import { decodeStrayEscapes } from "../text.js";
/**
 * A figure is a hand-built picture the model parameterises but never draws.
 *
 * Same bet as the check shells, for the same reason: the model emits JSON, we
 * ship the renderer. It is worth being explicit about why we don't just
 * generate an image, because "ask the model for a picture" is the obvious move
 * and it is wrong here.
 *
 * - A PNG cannot be validated. There is no server-side gate that tells you a
 *   generated image is on-topic, age-appropriate, or *correct*. A spec you can
 *   check; a picture you can only look at.
 * - Image models are worst at exactly what a learning app needs. A number line
 *   labelled 6, 7, 9, or a "7 × 8" array holding 54 dots, is worse than no
 *   picture at all — a child trusts the drawing over the text.
 * - It would cost a round trip on a critical path we already call too slow.
 *
 * A figure costs nothing extra instead: the model chooses one inside the answer
 * call it was already making.
 *
 * Adding a figure is one file here plus a renderer on the client keyed by the
 * same `kind`.
 */
/* ------------------------------------------------------------------ */
/* the wire shape                                                       */
/* ------------------------------------------------------------------ */
/**
 * Every figure travels as this one flat object, and the vocabulary is
 * deliberately tiny.
 *
 * Structured-output modes reject `oneOf`/`if`/`then` — the same limitation that
 * forces us to constrain a model to one check shell at a time. A per-kind union
 * is therefore not expressible, so all kinds share a small field vocabulary and
 * each one declares which fields it actually uses. Anything a kind doesn't use
 * is dropped on the way in.
 *
 * The cost of that is real: a kind cannot invent its own field without widening
 * this shape for everyone. That is the ceiling on this design, and when a figure
 * genuinely needs its own vocabulary it will want a second model call rather
 * than a wider object.
 */
export const figureSchema = z.object({
    kind: z.string(),
    /** One line under the picture. Not a repeat of the answer. */
    caption: z.string().max(70),
    rows: z.number().int().min(1).max(12).optional(),
    cols: z.number().int().min(1).max(12).optional(),
    /**
     * Raised from five to twelve for `contrast`, which fills a grid row-major and
     * needs headers plus rows. Kinds that want fewer cap themselves in `issues()`
     * — a ring of nine stages is unreadable however roomy the shared shape is.
     */
    labels: z.array(z.string().max(40)).max(12).optional(),
    /**
     * Magnitudes, parallel to `labels`. The one field added after the original
     * vocabulary, and deliberately: "how big is the Sun compared to Earth" is a
     * whole class of question that no arrangement of labels can answer, because
     * the answer IS the number.
     */
    values: z.array(z.number().finite()).max(12).optional(),
});
/** What a model may emit when the answer is better off with no picture. */
export const NO_FIGURE = "none";
const registry = new Map();
export function registerFigure(plugin) {
    if (registry.has(plugin.kind)) {
        throw new Error(`Figure "${plugin.kind}" is already registered.`);
    }
    registry.set(plugin.kind, plugin);
    return plugin;
}
export function getFigure(kind) {
    return registry.get(kind);
}
export function allFigures() {
    return [...registry.values()];
}
export function figureKinds() {
    return [...registry.keys()];
}
/**
 * The gate a figure must pass to reach a device.
 *
 * Deliberately more forgiving than `validateSpec`: a check that fails
 * validation costs the child their star, so it is worth a retry, but a figure
 * that fails costs them a picture. Callers drop it and render the text, which
 * is exactly what every answer did before figures existed. Nothing is worth
 * failing an answer over.
 */
export function validateFigure(candidate) {
    const parsed = figureSchema.safeParse(clampFigure(candidate));
    if (!parsed.success) {
        return {
            ok: false,
            issues: parsed.error.issues.map((i) => `${i.path.join(".") || "(root)"}: ${i.message}`),
        };
    }
    const plugin = getFigure(parsed.data.kind);
    if (!plugin) {
        return { ok: false, issues: [`Unknown figure "${parsed.data.kind}".`] };
    }
    const issues = plugin.issues(parsed.data);
    if (issues.length > 0)
        return { ok: false, issues };
    return { ok: true, figure: plugin.normalise(parsed.data) };
}
/**
 * Trims over-long text to the limits, before validation.
 *
 * Same reasoning as `normaliseSpec`: these limits are about fitting on a
 * tablet, not about truth. A cycle whose stage reads "Water rises into the air
 * as vapour" is correct work, and throwing the whole picture away over three
 * characters is the failure mode that made every figure quietly vanish the
 * first time this ran against a real model.
 */
function clampFigure(candidate) {
    if (typeof candidate !== "object" || candidate === null)
        return candidate;
    const raw = candidate;
    const out = { ...raw };
    if (typeof raw["caption"] === "string")
        out["caption"] = trimTo(decodeStrayEscapes(raw["caption"]), 70);
    if (Array.isArray(raw["values"])) {
        const values = raw["values"].filter((v) => typeof v === "number" && Number.isFinite(v));
        if (values.length > 0)
            out["values"] = values.slice(0, 12);
        else
            delete out["values"];
    }
    /*
      The wire shape makes every field required, so a cycle has to send `rows`
      and `cols` anyway and sends 0. Translating that back to "absent" belongs
      here, at the boundary — without it, `min(1)` rejected every figure that
      wasn't an array, which read from the outside as the model simply never
      choosing one.
    */
    for (const key of ["rows", "cols"]) {
        const value = raw[key];
        if (typeof value !== "number" || value < 1)
            delete out[key];
    }
    if (Array.isArray(raw["labels"])) {
        const labels = raw["labels"]
            .filter((l) => typeof l === "string" && l.trim().length > 0)
            .slice(0, 12)
            .map((l) => trimTo(decodeStrayEscapes(l), 40));
        if (labels.length > 0)
            out["labels"] = labels;
        else
            delete out["labels"];
    }
    return out;
}
/** Cuts at a word boundary where possible, so a label never ends mid-word. */
function trimTo(text, max) {
    const clean = text.trim().replace(/\s+/g, " ");
    if (clean.length <= max)
        return clean;
    const cut = clean.slice(0, max - 1);
    const lastSpace = cut.lastIndexOf(" ");
    return `${(lastSpace > max * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
/**
 * Validation for the generation path: `none` and anything malformed both mean
 * "render the text alone", which is a normal outcome rather than an error.
 */
export function figureOrNothing(candidate) {
    if (candidate === null || candidate === undefined)
        return undefined;
    const kind = candidate.kind;
    if (kind === NO_FIGURE || typeof kind !== "string")
        return undefined;
    const result = validateFigure(candidate);
    return result.ok ? result.figure : undefined;
}
/* ------------------------------------------------------------------ */
/* the model-facing contract                                            */
/* ------------------------------------------------------------------ */
/** The menu of kinds, for the answer and check prompts. */
export function figureMenu() {
    return [
        `   - "${NO_FIGURE}" — no picture. The right choice most of the time: pick it ` +
            `unless a picture genuinely does work the sentences cannot.`,
        ...allFigures().map((f) => `   - "${f.kind}" — ${f.chooseWhen}`),
    ].join("\n");
}
/**
 * The flat schema handed to a model. Every field is required because
 * structured-output modes demand it — a kind that doesn't use `labels` still
 * emits an empty array, and `normalise` throws it away.
 */
export function figureWireSchema() {
    return {
        type: "object",
        additionalProperties: false,
        required: ["kind", "caption", "rows", "cols", "labels", "values"],
        properties: {
            kind: { type: "string", enum: [NO_FIGURE, ...figureKinds()] },
            caption: {
                type: "string",
                description: "One short line under the picture, saying what to notice. Never a " +
                    'repeat of the answer. Empty string when kind is "none". Max 70 characters.',
            },
            rows: {
                type: "integer",
                description: 'Rows, for "array" only — no other kind uses it. 0 otherwise. ' +
                    "Between 1 and 12 when used.",
            },
            cols: {
                type: "integer",
                description: 'Columns, for "array". For "contrast", how many things are being ' +
                    "compared — that is, how many columns the grid has, either 2 or 3. " +
                    "0 for every other kind. Between 1 and 12 when used.",
            },
            labels: {
                type: "array",
                items: { type: "string" },
                description: "The text of the picture, and what it means depends on the kind: the " +
                    'stages of a "cycle"; the layers top to bottom of "parts"; the steps ' +
                    'in order of a "sequence"; the things being measured in a "scale"; ' +
                    'the waves being compared in a "wave". For "orbit" the FIRST label is ' +
                    "what sits in the middle and the rest go round it, nearest first. " +
                    'For "contrast" it is the grid read left to right, top row first: the ' +
                    "column headings, then each row of comparisons. Empty array when " +
                    "unused. Each label at most 34 characters, and shorter is better.",
            },
            values: {
                type: "array",
                items: { type: "number" },
                description: "The numbers, one per label and in the same order, used by two kinds " +
                    'and meaning something different in each. For "scale" they are real ' +
                    "magnitudes in one consistent unit — say the unit in the caption. " +
                    'For "wave" each is how many waves fit in the same space, so a bigger ' +
                    "number is a tighter, higher-pitched wave; these are relative and do " +
                    'not need a unit. Empty array for every other kind, including "orbit", ' +
                    "which takes no numbers at all.",
            },
        },
    };
}
//# sourceMappingURL=base.js.map